// NOTE: This string will be replaced on build time with the package version.
var version = "22.29.0";

/**
 * The project key must be between 2 and 36 characters long. It can only contain alphanumeric lowercase characters (a-z, 0-9),
 * up to two consecutive underscores (_) and hyphens (-). Leading and trailing underscore and hyphens are also not allowed.
 *
 * This regular expression has been generated using ChatGPT.
 * Explanation of the regular expression:
 * 1. ^: Asserts the start of the string.
 * 2. (?!.*(?:[-_]{3}|^[-_]|[-_]$)): Negative lookahead assertion ensures that there are no three consecutive hyphens or underscores ([-_]{3}), no leading hyphen or underscore (^[-_]), and no trailing hyphen or underscore ([-_]$).
 * 3. (?=.{2,36}$): Positive lookahead assertion ensures that the length of the string is between 2 and 36 characters.
 * 4. (?:[a-z1-9][a-z0-9]*)?: Non-capturing group matches an optional first character that cannot be '0' ([a-z1-9]) followed by zero or more lowercase alphanumeric characters ([a-z0-9]*).
 * 5. (?:[-_]{0,2}[a-z0-9]+)*: Non-capturing group matches zero, one, or two hyphens or underscores followed by one or more lowercase alphanumeric characters. This group can repeat zero or more times.
 * 6. $: Asserts the end of the string.
 */
const PROJECT_KEY_REGEX = /^(?!.*(?:[-_]{3}|^[-_]|[-_]$))(?=.{2,36}$)(?:[a-z1-9][a-z0-9]*)?(?:[-_]{0,2}[a-z0-9]+)*$/;

/**
 * The entryPointUriPath may be between 2 and 64 characters and only contain alphabetic lowercase characters,
 * non-consecutive underscores and hyphens. Leading and trailing underscore and hyphens are also not allowed.
 *
 * This regular expression has been generated using ChatGPT.
 * Explanation of the regular expression:
 * 1. ^: Asserts the start of the string.
 * 2. (?!.*(?:[-_]{2}|^[-_]|[-_]$)): Negative lookahead assertion ensures that there are no two consecutive hyphens or underscores ([-_]{2}), no leading hyphen or underscore (^[-_]), and no trailing hyphen or underscore ([-_]$).
 * 3. (?=.{2,64}$): Positive lookahead assertion ensures that the length of the string is between 2 and 64 characters.
 * 4. (?:[a-z1-9][a-z0-9]*)?: Non-capturing group matches an optional first character that cannot be '0' ([a-z1-9]) followed by zero or more lowercase alphanumeric characters ([a-z0-9]*).
 * 5. (?:[-_]{0,1}[a-z0-9]+)*: Non-capturing group matches zero, or one hyphen or underscore followed by one or more lowercase alphanumeric characters. This group can repeat zero or more times.
 * 6. $: Asserts the end of the string.
 */
const ENTRY_POINT_URI_PATH_REGEX = /^(?!.*(?:[-_]{2}|^[-_]|[-_]$))(?=.{2,64}$)(?:[a-z1-9][a-z0-9]*)?(?:[-_]{0,1}[a-z0-9]+)*$/;

/**
 * The permission group name may be between 2 and 64 characters and only contain alphanumeric lowercase characters and non-consecutive hyphens.
 * Leading and trailing hyphens are also not allowed.
 */
const PERMISSION_GROUP_NAME_REGEX = /^[^-#\W]([a-z]|[-](?![-])){0,62}[^-#\W]$/;

// DOM elements
const PORTALS_CONTAINER_ID = 'portals-container';
const PORTALS_CONTAINER_INDENTATION_SIZE = '48px';

// Links
const SUPPORT_PORTAL_URL = 'https://support.commercetools.com';

// Notification actions
const SHOW_LOADING = 'SHOW_LOADING';
const HIDE_LOADING = 'HIDE_LOADING';
const HIDE_ALL_PAGE_NOTIFICATIONS = 'HIDE_ALL_PAGE_NOTIFICATIONS';

// Notifications
const NOTIFICATION_DOMAINS = {
  GLOBAL: 'global',
  PAGE: 'page',
  SIDE: 'side'
};
const NOTIFICATION_KINDS_SIDE = {
  error: 'error',
  warning: 'warning',
  info: 'info',
  success: 'success'
};
const NOTIFICATION_KINDS_GLOBAL = {
  error: 'error',
  warning: 'warning',
  info: 'info',
  success: 'success',
  'unexpected-error': 'unexpected-error'
};
const NOTIFICATION_KINDS_PAGE = {
  error: 'error',
  warning: 'warning',
  info: 'info',
  success: 'success',
  'unexpected-error': 'unexpected-error',
  'api-error': 'api-error'
};
// Alias to `NOTIFICATION_DOMAINS` for backwards compatibility
const DOMAINS = NOTIFICATION_DOMAINS;
// Fallback string when there is no localized value
const NO_VALUE_FALLBACK = '- -';

// HTTP requests and responses
const STATUS_CODES = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  UNAUTHENTICATED: 299,
  NOT_FOUND: 404
};
const LOGOUT_REASONS = {
  USER: 'user',
  UNAUTHORIZED: 'unauthorized',
  INVALID: 'invalid',
  DELETED: 'deleted',
  NO_PROJECTS: 'no-projects'
};
const GRAPHQL_TARGETS = {
  MERCHANT_CENTER_BACKEND: 'mc',
  COMMERCETOOLS_PLATFORM: 'ctp',
  DASHBOARD_SERVICE: 'dashboard',
  CHANGE_HISTORY_SERVICE: 'change-history',
  PIM_INDEXER: 'pim-indexer',
  ORDER_INDEXER: 'order-indexer',
  SETTINGS_SERVICE: 'settings',
  ADMINISTRATION_SERVICE: 'administration'
};
const MC_API_PROXY_TARGETS = {
  COMMERCETOOLS_PLATFORM: 'ctp',
  MACHINE_LEARNING: 'ml',
  PIM_SEARCH: 'pim-search',
  ORDER_SEARCH: 'order-search',
  MC_METRICS: 'mc-metrics',
  IMPORT: 'import',
  EXPORT: 'export'
};

// Global application environment on window object

// Used for Custom Views, as we want to keep the `entryPointUriPath` value required in the runtime config.
const CUSTOM_VIEW_HOST_ENTRY_POINT_URI_PATH = '@@custom-view-host@@';
const SUPPORTED_HEADERS = {
  ACCEPT: 'Accept',
  ACCEPT_VERSION: 'Accept-version',
  AUTHORIZATION: 'Authorization',
  CONTENT_TYPE: 'Content-Type',
  X_APPLICATION_ID: 'X-Application-Id',
  X_CUSTOM_VIEW_ID: 'X-Custom-View-Id',
  X_CORRELATION_ID: 'X-Correlation-Id',
  X_FEATURE_FLAG: 'X-Feature-Flag',
  X_FORWARD_TO: 'X-Forward-To',
  X_FORWARD_TO_AUDIENCE_POLICY: 'X-Forward-To-Audience-Policy',
  X_FORWARD_TO_CLAIMS: 'X-Forward-To-Claims',
  X_GRAPHQL_TARGET: 'X-Graphql-Target',
  X_GRAPHQL_OPERATION_NAME: 'X-Graphql-Operation-Name',
  X_PROJECT_KEY: 'X-Project-Key',
  X_TEAM_ID: 'X-Team-Id',
  X_TOKEN_RETRY: 'X-Force-Token',
  X_USER_AGENT: 'X-User-Agent'
};
const STORAGE_KEYS = {
  NONCE: 'nonce',
  IS_AUTHENTICATED: 'isAuthenticated',
  SESSION_TOKEN: 'sessionToken',
  SESSION_SCOPE: 'sessionScope',
  ACTIVE_PROJECT_KEY: 'activeProjectKey',
  ACTIVE_TEAM_ID: 'activeTeamId',
  SELECTED_DATA_LOCALE: 'selectedDataLocale',
  IS_FORCED_MENU_OPEN: 'isForcedMenuOpen',
  LOGIN_STRATEGY: 'loginStrategy'
};
const HTTP_SECURITY_HEADERS = {
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
  'X-XSS-Protection': '1; mode=block',
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'SAMEORIGIN',
  'Referrer-Policy': 'same-origin'
};

// Custom Views events (messages sent between the host application and the custom view)
const CUSTOM_VIEWS_EVENTS_NAMES = {
  CUSTOM_VIEW_BOOTSTRAP: 'custom-view-bootstrap',
  CUSTOM_VIEW_INITIALIZATION: 'custom-view-initialization',
  CUSTOM_VIEW_CLOSE: 'custom-view-close'
};
const CUSTOM_VIEWS_EVENTS_META = {
  HOST_APPLICATION_CODE: 'mc-host-application',
  CUSTOM_VIEW_KEY_PREFIX: 'custom-view-'
};

// SSO
const ORGANIZATION_GENERAL_ERROR = 'organizationGeneralError';
const LOGIN_STRATEGY_DEFAULT = 'default';
const LOGIN_STRATEGY_OIDC = 'oidc';
const LOGIN_STRATEGY_SSO = 'sso';

/*
 * This file contains a list of all feature toggles used within
 * the app shell. Each feature toggle should be a constant.
 *
 * ```
 * export const FEATURE_TOGGLE_NAME = 'featureToggleName';
 * export const FLAGS = {
 *   [FEATURE_TOGGLE_NAME]: false,
 * };
 * ```
 *
 * NOTE:
 *   Default values are not yet passed to flopflip and it has to be decided
 *   how the integrate with the plugin API.
 *   The default values would be used whenever feature toggles have not been
 *   fetched from LaunchDarkly (without default values flopflip will default
 *   them to off until fetched).
 */

const CUSTOM_VIEWS = 'enableCustomViews';
const FLAGS = {};

// Long-lived feature flags, defined in the MC API.
const DEFAULT_FLAGS = {
  [CUSTOM_VIEWS]: {
    value: true
  }
};

var featureToggles = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CUSTOM_VIEWS: CUSTOM_VIEWS,
  FLAGS: FLAGS,
  DEFAULT_FLAGS: DEFAULT_FLAGS
});

export { CUSTOM_VIEWS_EVENTS_META, CUSTOM_VIEWS_EVENTS_NAMES, CUSTOM_VIEW_HOST_ENTRY_POINT_URI_PATH, DOMAINS, ENTRY_POINT_URI_PATH_REGEX, GRAPHQL_TARGETS, HIDE_ALL_PAGE_NOTIFICATIONS, HIDE_LOADING, HTTP_SECURITY_HEADERS, LOGIN_STRATEGY_DEFAULT, LOGIN_STRATEGY_OIDC, LOGIN_STRATEGY_SSO, LOGOUT_REASONS, MC_API_PROXY_TARGETS, NOTIFICATION_DOMAINS, NOTIFICATION_KINDS_GLOBAL, NOTIFICATION_KINDS_PAGE, NOTIFICATION_KINDS_SIDE, NO_VALUE_FALLBACK, ORGANIZATION_GENERAL_ERROR, PERMISSION_GROUP_NAME_REGEX, PORTALS_CONTAINER_ID, PORTALS_CONTAINER_INDENTATION_SIZE, PROJECT_KEY_REGEX, SHOW_LOADING, STATUS_CODES, STORAGE_KEYS, SUPPORTED_HEADERS, SUPPORT_PORTAL_URL, featureToggles as featureFlags, version };
